import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import {
  ListOrdered,
  FileText,
  Database,
  ScanLine,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Settings,
  Calculator,
  Loader2,
} from 'lucide-react';
import type { DashboardProject } from '../project/types/project';
import {
  PIPEFITTER_TAB_LIST_WRAPPER_CLASS,
  PIPEFITTER_TAB_TRIGGER_CLASS,
} from './constants/uiConstants';
import { Button } from '../../components/ui/button';
import { PipeSelector } from '../../components/ui/PipeSelector';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/custom-dropdown-menu';
import { Sequence, type SequenceHandle } from './components/Sequence';
import { Reports } from './components/Reports';
import { PipeDetails } from './components/PipeDetails';
import { PipefitterViewModeToggle, type PipefitterDetailsViewMode } from './components/PipefitterViewModeToggle';
import { PipeStatusOverviewPanel } from './components/PipeStatusOverviewPanel';
import { PipeGroupPipesDialog } from './components/PipeGroupPipesDialog';
import { OptifitResults } from './components/OptifitResults';
import {
  GenerateSequenceModal,
  type GenerateSequenceFormValues,
  type PipeSpecificationForSequence,
} from './components/GenerateSequenceModal';
import { PipefitterReportsModal } from './components/PipefitterReportsModal';
import { PipeDetailsSelectionItem } from './components/PipeFilter';
import {
  pipefitterPipeBasedGenerateSequence,
  pipefitterMinMaxCalculation,
  pipefitterSetMeanMaxCalculationStatus,
} from '../../redux/Pipefitter/actions';
import { PIPEFITTER_GET_CONFIGURATION } from '../../redux/Pipefitter/types';
import type { Spread } from './types';
import { convertValue } from '../../utils/unitUtils';
import { PipefitterReportVersionsModal } from './components/PipefitterReportVersionsModal';
import { PipeDataReportGenerateModal } from './components/PipeDataReportGenerateModal';
import { PipeCompatibilityReportGenerateModal } from './components/PipeCompatibilityReportGenerateModal';
import { GenerateReportTypeModal } from './components/GenerateReportTypeModal';
import { RandomPipeCompatibilityModal } from './components/randompipecompat/RandomPipeCompatibilityModal';
import { RandomPipeCompatHistoryModal } from './components/randompipecompat/RandomPipeCompatHistoryModal';
import { ProjectSettingsModal } from './components/ProjectSettingsModal';
import { fetchProjectInformation, isMeanMaxCalculationRunning } from './api/projectInformation';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../components/ui/dialog';

interface ProjectPipefitterProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
}

const SUBTABS = [
  { key: 'pipeDetails', label: 'Pipe Details', icon: Database },
  { key: 'sequence', label: 'Sequence', icon: ListOrdered },
  { key: 'optifit', label: 'Optifit', icon: ScanLine },
  { key: 'visualization', label: 'Status', icon: FileText },
] as const;

/** Pipe Size dropdown: fetch all pipe sizes / groups for the project. */
export const ALL_PIPE_SIZES_VALUE = '__all__';

export const ALL_PIPE_SIZES_OPTION = {
  pipeId: null as null,
  isAllPipeSizes: true as const,
  displayName: 'All',
  pipeName: 'All',
};

function extractPipeSizes(setupData: Record<string, any>): any[] {
  const pipeSizes: any[] = [];
  Object.values(setupData || {}).forEach((setup: any) => {
    if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
      setup.pipeSizes.forEach((pipe: any) => {
        const groupId =
          pipe.groupId != null && String(pipe.groupId).trim() !== ''
            ? String(pipe.groupId).trim()
            : '';
        const baseDisplayName =
          setup.projectSubType === 'none' && setup.projectType !== 'fabrication'
            ? `${setup.projectType === 'pipeline' ? 'Pipeline' : setup.projectType === 'others' ? 'Others' : setup.projectType} ${pipe.pipeName} (${pipe.pipeSize} x ${pipe.wallThickness})`
            : `${setup.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' : setup.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' : setup.projectSubType === 'bulkhead' ? 'Bulk Head' : setup.projectSubType} ${pipe.pipeName} (${pipe.pipeSize} x ${pipe.wallThickness})`;
        const displayName = groupId ? `${baseDisplayName} · ${groupId}` : baseDisplayName;

        const highlightRaw = pipe.highlightType ?? pipe.highlight_type ?? 'Range';
        const highlightType: 'tolerance' | 'Range' = highlightRaw === 'tolerance' ? 'tolerance' : 'Range';
        const pipeSizeMm = convertValue(pipe.pipeSize ?? 0, 'Inches', 'mm');
        /** Setup/DB Nominal OD is mm-only — use as-is, do not convert from pipe size. */
        const setupOdRaw =
          pipe.nominalOd != null && pipe.nominalOd !== '' ? Number(pipe.nominalOd) : NaN;
        const nominalOdMm =
          Number.isFinite(setupOdRaw) && setupOdRaw > 0 ? setupOdRaw : undefined;

        let nominalIdMax: number;
        let nominalIdMin: number;
        let nominalOdMax: number;
        let nominalOdMin: number;
        if (highlightType === 'tolerance') {
          nominalIdMax = pipeSizeMm + Number(pipe.nominalIdMax ?? 0);
          nominalIdMin = pipeSizeMm - Number(pipe.nominalIdMin ?? 0);
          nominalOdMax = pipeSizeMm + Number(pipe.nominalOdMax ?? 0);
          nominalOdMin = pipeSizeMm - Number(pipe.nominalOdMin ?? 0);
        } else {
          nominalIdMax = Number(pipe.nominalIdMax ?? 0);
          nominalIdMin = Number(pipe.nominalIdMin ?? 0);
          nominalOdMax = Number(pipe.nominalOdMax ?? 0);
          nominalOdMin = Number(pipe.nominalOdMin ?? 0);
        }

        pipeSizes.push({
          pipeId: pipe.pipeId,
          pipeName: pipe.pipeName,
          groupId,
          projectSubType: setup.projectSubType,
          projectType: setup.projectType,
          projectId: setup.projectId,
          projectTypeId: setup.projectTypeId,
          size: pipe.pipeSize ?? pipe.size ?? 0,
          wallThickness: pipe.wallThickness ?? 0,
          displayName,
          nominalPipeLength:
            pipe.nominalPipeLength != null && pipe.nominalPipeLength !== ''
              ? Number(pipe.nominalPipeLength)
              : undefined,
          targetNumberOfPipes:
            pipe.targetNumberOfPipes != null && pipe.targetNumberOfPipes !== ''
              ? Number(pipe.targetNumberOfPipes)
              : undefined,
          targetPipelineLength:
            pipe.targetPipelineLength != null && pipe.targetPipelineLength !== ''
              ? Number(pipe.targetPipelineLength)
              : undefined,
          highlightType,
          nominalId: pipeSizeMm,
          nominalOd: nominalOdMm,
          nominalIdMax,
          nominalIdMin,
          nominalOdMax,
          nominalOdMin,
          oORIDTolerance: pipe.oORIDTolerance ?? pipe.oORIDTolrence ?? 0,
          oORODTolerance: pipe.oORODTolerance ?? pipe.oORODTolrence ?? 0,
          weldSeamExclusionDeg:
            pipe.weldSeamExclusionDeg != null
              ? Number(pipe.weldSeamExclusionDeg)
              : pipe.weld_seam_exclusion_deg != null
                ? Number(pipe.weld_seam_exclusion_deg)
                : undefined,
          sixOclockExclusionDeg:
            pipe.sixOclockExclusionDeg != null
              ? Number(pipe.sixOclockExclusionDeg)
              : pipe.sixOClockExclusionDeg != null
                ? Number(pipe.sixOClockExclusionDeg)
                : pipe.six_oclock_exclusion_deg != null
                  ? Number(pipe.six_oclock_exclusion_deg)
                  : undefined,
          pipeMaterial: pipe.pipeMaterial ?? undefined,
          pipeRotation: pipe.pipeRotation ?? undefined,
          /** Setup/DB HiLo is mm-only — keep as hiloThresholdMm with no conversion. */
          hiloThresholdMm:
            pipe.hiloThresholdMm != null
              ? Number(pipe.hiloThresholdMm)
              : pipe.hiLoThresholdMm != null
                ? Number(pipe.hiLoThresholdMm)
                : pipe.hiloThreshold != null
                  ? Number(pipe.hiloThreshold)
                  : pipe.hilo_threshold != null
                    ? Number(pipe.hilo_threshold)
                    : undefined,
          hiloThreshold:
            pipe.hiloThresholdMm != null
              ? Number(pipe.hiloThresholdMm)
              : pipe.hiLoThresholdMm != null
                ? Number(pipe.hiLoThresholdMm)
                : pipe.hiloThreshold != null
                  ? Number(pipe.hiloThreshold)
                  : pipe.hilo_threshold != null
                    ? Number(pipe.hilo_threshold)
                    : undefined,
          pipeType: pipe.pipeType ?? undefined,
        });
      });
    }
  });
  return pipeSizes;
}

export interface SequenceOption {
  label: string;
  value: string;
  sequenceName: string;
}

export interface SequenceSelectionRequest {
  selectionType: 'spread' | 'subSpread';
  id: number;
  token: number;
}

export function nominalOdMmFromPipeSpec(
  pipe: {
    nominalOd?: number;
    nominalODMm?: number;
    nominalOdMax?: number;
    nominalOdMin?: number;
  } | null | undefined
): number {
  if (!pipe) return 508;
  // Setup/DB values only — never convert (no ×25.4).
  const od = Number(pipe.nominalOd ?? pipe.nominalODMm ?? 0);
  if (od > 0) return od;
  const max = Number(pipe.nominalOdMax ?? 0);
  if (max > 0) return max;
  const min = Number(pipe.nominalOdMin ?? 0);
  if (min > 0) return min;
  return 508;
}

export function nominalPipeLengthFtFromSpec(pipe: { nominalPipeLength?: number } | null | undefined): number {
  if (!pipe) return 40;
  // Setup / pipefitter DB stores nominalPipeLength in ft
  const ft = Number(pipe.nominalPipeLength ?? 0);
  return ft > 0 ? ft : 40;
}

export function targetNumberOfPipesFromSpec(
  pipe: { targetNumberOfPipes?: number } | null | undefined,
  selectedPipeCount: number
): number {
  const fromSetup = Number(pipe?.targetNumberOfPipes ?? 0);
  if (fromSetup > 0) return fromSetup;
  return Math.max(selectedPipeCount, 1);
}

export function targetPipelineLengthFtFromSpec(
  pipe: { targetPipelineLength?: number } | null | undefined,
  nominalPipeLengthFt: number,
  targetNumberOfPipes: number
): number {
  // Setup stores targetPipelineLength in ft
  const fromSetup = Number(pipe?.targetPipelineLength ?? 0);
  if (fromSetup > 0) return fromSetup;
  return nominalPipeLengthFt * targetNumberOfPipes;
}

function optionalFiniteNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function weldSeamExclusionDegFromSpec(
  pipe: { weldSeamExclusionDeg?: number; weld_seam_exclusion_deg?: number } | null | undefined
): number | undefined {
  return optionalFiniteNumber(pipe?.weldSeamExclusionDeg ?? pipe?.weld_seam_exclusion_deg);
}

export function sixOclockExclusionDegFromSpec(
  pipe: {
    sixOclockExclusionDeg?: number;
    sixOClockExclusionDeg?: number;
    six_oclock_exclusion_deg?: number;
  } | null | undefined
): number | undefined {
  return optionalFiniteNumber(
    pipe?.sixOclockExclusionDeg ??
      pipe?.sixOClockExclusionDeg ??
      pipe?.six_oclock_exclusion_deg
  );
}

export function hiloThresholdMmFromSpec(
  pipe: {
    hiloThresholdMm?: number;
    hiloThreshold?: number;
    hilo_threshold?: number;
    hiLoThresholdMm?: number;
  } | null | undefined
): number | undefined {
  // DB / setup stores mm only — prefer hiloThresholdMm, no unit conversion.
  return optionalFiniteNumber(
    pipe?.hiloThresholdMm ??
      pipe?.hiLoThresholdMm ??
      pipe?.hiloThreshold ??
      pipe?.hilo_threshold
  );
}

export function pipeRotationFromSpec(
  pipe: { pipeRotation?: string } | null | undefined
): GenerateSequenceFormValues['pipeRotation'] | undefined {
  const raw = String(pipe?.pipeRotation ?? '').trim().toLowerCase();
  if (!raw) return undefined;
  if (raw.includes('anti')) return 'antiClockwise';
  if (raw.includes('clock')) return 'clockwise';
  if (raw === 'any') return 'any';
  return undefined;
}

export function pipeTypeFromSpec(
  pipe: { pipeType?: string } | null | undefined
): GenerateSequenceFormValues['pipeType'] | undefined {
  const raw = String(pipe?.pipeType ?? '').trim().toLowerCase();
  if (!raw) return undefined;
  return raw === 'double' ? 'double' : 'single';
}

export function buildGenerateSequenceInitialValuesFromPipe(
  selectedPipe: Record<string, unknown> | null | undefined,
  selectedPipeCount: number
): Partial<GenerateSequenceFormValues> {
  const len = nominalPipeLengthFtFromSpec(selectedPipe);
  const od = nominalOdMmFromPipeSpec(selectedPipe);
  const targetNumberOfPipes = targetNumberOfPipesFromSpec(selectedPipe, selectedPipeCount);
  const targetPipelineLengthFt = targetPipelineLengthFtFromSpec(
    selectedPipe,
    len,
    targetNumberOfPipes
  );
  const weldSeamExclusionMm = weldSeamExclusionDegFromSpec(selectedPipe);
  const exclusion6OClockMm = sixOclockExclusionDegFromSpec(selectedPipe);
  const hiloThresholdMm = hiloThresholdMmFromSpec(selectedPipe);
  const pipeRotation = pipeRotationFromSpec(selectedPipe);
  const pipeType = pipeTypeFromSpec(selectedPipe);
  const pipeMaterial = String(selectedPipe?.pipeMaterial ?? '').trim();

  return {
    nominalPipeLengthFt: len,
    targetNumberOfPipes,
    targetPipelineLengthFt,
    nominalODMm: od,
    ...(weldSeamExclusionMm != null ? { weldSeamExclusionMm } : {}),
    ...(exclusion6OClockMm != null ? { exclusion6OClockMm } : {}),
    ...(hiloThresholdMm != null ? { hiloThresholdMm } : {}),
    ...(pipeRotation ? { pipeRotation } : {}),
    ...(pipeType ? { pipeType } : {}),
    ...(pipeMaterial ? { pipeMaterial } : {}),
  };
}

function buildSequenceOptions(configurationList: Spread[]): SequenceOption[] {
  const options: SequenceOption[] = [];
  (configurationList || []).forEach((spread: Spread) => {
    options.push({
      label: `Sequence: ${spread.spreadName}`,
      value: `spread:${spread.id}`,
      sequenceName: spread.spreadName ?? '',
    });
    (spread.subSpreads || []).forEach((sub: { id: number; subSpreadName?: string }) => {
      options.push({
        label: `Sequence: ${spread.spreadName} > ${sub.subSpreadName ?? 'Sub-sequence'}`,
        value: `subSpread:${sub.id}`,
        sequenceName: sub.subSpreadName ?? spread.spreadName ?? '',
      });
    });
  });
  return options;
}

export const ProjectPipefitter: React.FC<ProjectPipefitterProps> = ({ project, canEdit, canReport }) => {
  const dispatch = useDispatch();
  const { pipeDetails, isPipeDetailsLoading } = useSelector((state: any) => state.setupData || {});
  const configurationList = useSelector((state: any) => state.pipefitterConfiguration?.configurationList || []);
  const isSequenceLoading = useSelector((state: any) => state.pipefitterSequence?.isSequenceLoading ?? false);
  const meanMaxStatusProjectNumber = useSelector(
    (state: any) => state.pipefitterProject?.meanMaxStatusProjectNumber ?? null
  );
  const meanMaxCalculationStatus = useSelector(
    (state: any) => state.pipefitterProject?.meanMaxCalculationStatus ?? null
  );
  const [activeSubtab, setActiveSubtab] = useState<string>('pipeDetails');
  const [pipeSizesData, setPipeSizesData] = useState<any[]>([]);
  /** Index into pipeSizesData, or ALL_PIPE_SIZES_VALUE for project-wide table. */
  const [selectedPipeIndex, setSelectedPipeIndex] = useState<number | typeof ALL_PIPE_SIZES_VALUE>(0);
  const [selectedPipe, setSelectedPipe] = useState<any | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [selectedPipeDetailsFromTable, setSelectedPipeDetailsFromTable] = useState<PipeDetailsSelectionItem[]>([]);
  const [pipeDetailsViewMode, setPipeDetailsViewMode] = useState<PipefitterDetailsViewMode>('table');
  const [overviewGroupDialogOpen, setOverviewGroupDialogOpen] = useState(false);
  const [overviewGroupIds, setOverviewGroupIds] = useState<string[]>([]);
  const [overviewPipeIds, setOverviewPipeIds] = useState<Array<string | number>>([]);
  const [overviewRefreshToken, setOverviewRefreshToken] = useState(0);
  const [sequenceSelectionRequest, setSequenceSelectionRequest] = useState<SequenceSelectionRequest | null>(null);
  const [pendingGeneratedSequenceName, setPendingGeneratedSequenceName] = useState<string>('');
  const sequenceRef = useRef<SequenceHandle | null>(null);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [pipeDataReportGenerateModalOpen, setPipeDataReportGenerateModalOpen] = useState(false);
  const [pipeCompatibilityReportGenerateModalOpen, setPipeCompatibilityReportGenerateModalOpen] =
    useState(false);
  const [generateReportTypeModalOpen, setGenerateReportTypeModalOpen] = useState(false);
  const [randomPipeCompatibilityModalOpen, setRandomPipeCompatibilityModalOpen] = useState(false);
  const [randomPipeCompatHistoryModalOpen, setRandomPipeCompatHistoryModalOpen] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMeanMaxConfirmModal, setShowMeanMaxConfirmModal] = useState(false);
  const [isMeanMaxSubmitting, setIsMeanMaxSubmitting] = useState(false);
  const [isMeanMaxStatusChecking, setIsMeanMaxStatusChecking] = useState(false);

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const updateTabScrollButtons = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollTabsLeft(scrollLeft > 1);
    setCanScrollTabsRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    updateTabScrollButtons();
    const onScroll = () => updateTabScrollButtons();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateTabScrollButtons());
    ro.observe(el);
    const tabList = el.firstElementChild;
    if (tabList) ro.observe(tabList);
    window.addEventListener('resize', updateTabScrollButtons);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateTabScrollButtons);
    };
  }, [updateTabScrollButtons]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => updateTabScrollButtons());
    return () => cancelAnimationFrame(frame);
  }, [activeSubtab, updateTabScrollButtons]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const activeTrigger = container.querySelector<HTMLElement>('[data-state="active"]');
    if (activeTrigger) {
      const { offsetLeft, offsetWidth } = activeTrigger;
      const { scrollLeft, clientWidth } = container;
      const isFullyVisible =
        offsetLeft >= scrollLeft &&
        offsetLeft + offsetWidth <= scrollLeft + clientWidth;

      if (!isFullyVisible) {
        activeTrigger.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
      updateTabScrollButtons();
    }
  }, [activeSubtab, updateTabScrollButtons]);

  const handleScrollTabsLeft = () => {
    tabsContainerRef.current?.scrollBy({ left: -160, behavior: 'smooth' });
  };

  const handleScrollTabsRight = () => {
    tabsContainerRef.current?.scrollBy({ left: 160, behavior: 'smooth' });
  };

  const projectNumber = project?.project?.projectNumber ?? '';
  const projectName = project?.project?.projectName ?? '';
  const projectId = project?.project?.projectId;
  const canGenerateReports = canReport ?? canEdit;
  const selectedGroupId =
    selectedPipe != null && selectedPipe.groupId != null
      ? String(selectedPipe.groupId).trim()
      : undefined;

  const refreshMeanMaxStatus = useCallback(async () => {
    const trimmed = projectNumber?.trim();
    if (!trimmed) return;
    try {
      const info = await fetchProjectInformation(trimmed);
      dispatch(
        pipefitterSetMeanMaxCalculationStatus({
          projectNumber: trimmed,
          status: info?.meanMaxCalculationStatus ?? null,
          error: null,
        })
      );
    } catch (err) {
      dispatch(
        pipefitterSetMeanMaxCalculationStatus({
          projectNumber: trimmed,
          status: null,
          error: err instanceof Error ? err.message : 'Failed to load mean/max status',
        })
      );
    }
  }, [dispatch, projectNumber]);

  const meanMaxStatusForProject =
    projectNumber?.trim() && meanMaxStatusProjectNumber === projectNumber.trim()
      ? meanMaxCalculationStatus
      : null;
  const isMeanMaxAlreadyRunning = isMeanMaxCalculationRunning(meanMaxStatusForProject);

  const handleOpenMeanMaxConfirmModal = () => {
    setShowMeanMaxConfirmModal(true);
    setIsMeanMaxStatusChecking(true);
    void (async () => {
      try {
        await refreshMeanMaxStatus();
      } finally {
        setIsMeanMaxStatusChecking(false);
      }
    })();
  };

  const handleConfirmMeanMaxCalculation = () => {
    const trimmed = projectNumber?.trim();
    if (!trimmed || isMeanMaxAlreadyRunning || isMeanMaxStatusChecking) return;
    setIsMeanMaxSubmitting(true);
    dispatch(
      pipefitterMinMaxCalculation(
        {
          projectNumber: trimmed,
          ...(selectedGroupId !== undefined ? { groupId: selectedGroupId } : {}),
        },
        () => {
          setIsMeanMaxSubmitting(false);
          setShowMeanMaxConfirmModal(false);
          dispatch(
            pipefitterSetMeanMaxCalculationStatus({
              projectNumber: trimmed,
              status: 'InProgress',
              error: null,
            })
          );
        },
        () => {
          setIsMeanMaxSubmitting(false);
          void refreshMeanMaxStatus();
        }
      )
    );
  };

  const selectedPipeStatusIds = useMemo(
    () => selectedPipeDetailsFromTable.map((p) => p.id),
    [selectedPipeDetailsFromTable]
  );
  const pipeIds = useMemo(
    () => selectedPipeDetailsFromTable.map((p) => p.pipeID),
    [selectedPipeDetailsFromTable]
  );
  const sequenceOptions = useMemo(
    () => buildSequenceOptions(configurationList as Spread[]),
    [configurationList]
  );

  useEffect(() => {
    if (!pendingGeneratedSequenceName || !configurationList?.length) return;
    const exactSpread = (configurationList as Spread[]).find(
      (spread) => spread.spreadName?.trim() === pendingGeneratedSequenceName.trim()
    );
    if (exactSpread?.id) {
      setSequenceSelectionRequest({
        selectionType: 'spread',
        id: exactSpread.id,
        token: Date.now(),
      });
      setPendingGeneratedSequenceName('');
      return;
    }
    for (const spread of configurationList as Spread[]) {
      const matchedSub = (spread.subSpreads || []).find(
        (sub) => sub.subSpreadName?.trim() === pendingGeneratedSequenceName.trim()
      );
      if (matchedSub?.id) {
        setSequenceSelectionRequest({
          selectionType: 'subSpread',
          id: matchedSub.id,
          token: Date.now(),
        });
        setPendingGeneratedSequenceName('');
        return;
      }
    }
  }, [configurationList, pendingGeneratedSequenceName]);

  const extractedPipeSizes = useMemo(() => {
    if (pipeDetails && Object.keys(pipeDetails).length > 0) {
      return extractPipeSizes(pipeDetails);
    }
    return [];
  }, [pipeDetails]);

  useEffect(() => {
    if (extractedPipeSizes.length > 0) {
      setPipeSizesData(extractedPipeSizes);
      setSelectedPipeIndex((prev) => {
        if (prev === ALL_PIPE_SIZES_VALUE) return ALL_PIPE_SIZES_VALUE;
        const idx = typeof prev === 'number' ? prev : 0;
        return Math.min(idx, extractedPipeSizes.length - 1);
      });
    } else {
      setPipeSizesData([]);
      setSelectedPipeIndex(0);
      setSelectedPipe(null);
    }
  }, [extractedPipeSizes]);

  useEffect(() => {
    if (pipeSizesData.length === 0) {
      setSelectedPipe(null);
      return;
    }
    if (selectedPipeIndex === ALL_PIPE_SIZES_VALUE) {
      setSelectedPipe({ ...ALL_PIPE_SIZES_OPTION });
      return;
    }
    setSelectedPipe(pipeSizesData[selectedPipeIndex] ?? null);
  }, [pipeSizesData, selectedPipeIndex]);

  // One min/max status fetch per project after pipe sizes load — cached in Redux for Generate Report.
  useEffect(() => {
    const trimmed = projectNumber?.trim();
    if (!trimmed || pipeSizesData.length === 0) return;
    if (meanMaxStatusProjectNumber === trimmed) return;

    let cancelled = false;
    (async () => {
      try {
        const info = await fetchProjectInformation(trimmed);
        if (cancelled) return;
        dispatch(
          pipefitterSetMeanMaxCalculationStatus({
            projectNumber: trimmed,
            status: info?.meanMaxCalculationStatus ?? null,
            error: null,
          })
        );
      } catch (err) {
        if (cancelled) return;
        dispatch(
          pipefitterSetMeanMaxCalculationStatus({
            projectNumber: trimmed,
            status: null,
            error:
              err instanceof Error
                ? err.message
                : 'Failed to check min/max calculation status.',
          })
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectNumber, pipeSizesData.length, meanMaxStatusProjectNumber, dispatch]);

  const pipeSpecificationForSequence = useMemo((): PipeSpecificationForSequence | null => {
    if (!selectedPipe) return null;
    const len = nominalPipeLengthFtFromSpec(selectedPipe);
    const targetNumberOfPipes = targetNumberOfPipesFromSpec(selectedPipe, pipeIds.length);
    const exclusion6OClockDeg = sixOclockExclusionDegFromSpec(selectedPipe);
    const weldSeamExclusionDeg = weldSeamExclusionDegFromSpec(selectedPipe);
    const hiloThresholdMm = hiloThresholdMmFromSpec(selectedPipe);
    return {
      nominalPipeLengthFt: len,
      nominalODMm: nominalOdMmFromPipeSpec(selectedPipe),
      targetNumberOfPipes,
      targetPipelineLengthFt: targetPipelineLengthFtFromSpec(
        selectedPipe,
        len,
        targetNumberOfPipes
      ),
      ...(exclusion6OClockDeg != null ? { exclusion6OClockDeg } : {}),
      ...(weldSeamExclusionDeg != null ? { weldSeamExclusionDeg } : {}),
      ...(hiloThresholdMm != null ? { hiloThresholdMm } : {}),
    };
  }, [selectedPipe, pipeIds.length]);

  const generateSequenceInitialValues = useMemo(
    () => buildGenerateSequenceInitialValuesFromPipe(selectedPipe, pipeIds.length),
    [selectedPipe, pipeIds.length]
  );

  const handleOpenGenerateReportPicker = () => {
    setGenerateReportTypeModalOpen(true);
  };

  const handleSelectPipeDataReport = () => {
    setGenerateReportTypeModalOpen(false);
    setPipeDataReportGenerateModalOpen(true);
  };

  const handleSelectPipeCompatibilityReport = () => {
    if (!projectNumber) return;
    setGenerateReportTypeModalOpen(false);
    setPipeCompatibilityReportGenerateModalOpen(true);
  };

  const handleSelectRandomPipeCompatibilityReport = () => {
    setGenerateReportTypeModalOpen(false);
    setRandomPipeCompatibilityModalOpen(true);
  };

  if (isPipeDetailsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
          <span className="text-gray-600 text-lg">Loading Pipe Details data...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-4 flex w-full items-center justify-between gap-3">
          {pipeDetailsViewMode === 'overview' ? (
            <h2 className="text-[16px] font-semibold text-blueprint-blue tracking-tight">
              Project Overview
            </h2>
          ) : (
            <div />
          )}
          {/* <PipefitterViewModeToggle
            value={pipeDetailsViewMode}
            onChange={(mode) => {
              setPipeDetailsViewMode(mode);
              if (mode === 'overview') setActiveSubtab('pipeDetails');
            }}
          /> */}
        </div>

        {pipeDetailsViewMode === 'overview' ? (
          <PipeStatusOverviewPanel
            project={project}
            pipeSizeOptions={Array.from(
              new Map(
                pipeSizesData.map((pipe) => [
                  String(pipe.pipeId),
                  {
                    pipeId: pipe.pipeId,
                    label: pipe.displayName ?? pipe.pipeName ?? String(pipe.pipeId),
                  },
                ])
              ).values()
            )}
            refreshToken={overviewRefreshToken}
            onOpenGroups={({ groupIds, pipeIds: openPipeIds }) => {
              setOverviewGroupIds(groupIds);
              setOverviewPipeIds(openPipeIds);
              setOverviewGroupDialogOpen(true);
            }}
          />
        ) : (
          <>
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                <Label className="shrink-0 whitespace-nowrap text-[12px] font-medium text-text-grey">Pipe Size:</Label>
                <PipeSelector
                  triggerWidth="w-full sm:w-[400px]"
                  value={
                    pipeSizesData.length === 0
                      ? ''
                      : selectedPipeIndex === ALL_PIPE_SIZES_VALUE
                        ? ALL_PIPE_SIZES_VALUE
                        : selectedPipeIndex.toString()
                  }
                  onChange={(value: string) => {
                    if (value === ALL_PIPE_SIZES_VALUE) {
                      setSelectedPipeIndex(ALL_PIPE_SIZES_VALUE);
                      return;
                    }
                    const idx = Number(value);
                    setSelectedPipeIndex(idx);
                    if (pipeSizesData[idx]) setSelectedPipe(pipeSizesData[idx]);
                  }}
                  placeholder="Select pipe size"
                  options={[
                    { value: ALL_PIPE_SIZES_VALUE, label: 'All' },
                    ...pipeSizesData.map((pipe, index) => ({
                      value: index.toString(),
                      label: pipe.displayName,
                    })),
                  ]}
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2 bg-hero-red text-white hover:bg-hero-red-60"
                  onClick={() => {
                    dispatch({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber } });
                    setIsGenerateModalOpen(true);
                  }}
                >
                  <ListOrdered className="h-4 w-4" />
                  Generate Sequence
                </Button>
                {(canReport || canEdit) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E5EA] bg-white transition-colors hover:bg-page-bg">
                      <MoreHorizontal className="h-4 w-4 text-text-grey" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canGenerateReports && (
                        <DropdownMenuItem onClick={handleOpenGenerateReportPicker}>
                          <FileText className="h-4 w-4 mr-2" />
                          Reports
                        </DropdownMenuItem>
                      )}
                      {canReport && (
                        <DropdownMenuItem onClick={() => setShowVersionsModal(true)}>
                          <FileText className="h-4 w-4 mr-2" />
                          Report History
                        </DropdownMenuItem>
                      )}
                      {canReport && (
                        <DropdownMenuItem onClick={() => setRandomPipeCompatHistoryModalOpen(true)}>
                          <FileText className="h-4 w-4 mr-2" />
                          Random Pipe Compat History
                        </DropdownMenuItem>
                      )}
                      {canEdit && (
                        <DropdownMenuItem onClick={handleOpenMeanMaxConfirmModal}>
                          <Calculator className="h-4 w-4 mr-2" />
                          Recalculate Mean/Max
                        </DropdownMenuItem>
                      )}
                      {canEdit && (
                        <DropdownMenuItem onClick={() => setShowSettingsModal(true)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Settings
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            <Tabs value={activeSubtab} onValueChange={setActiveSubtab} className="w-full min-w-0">
              <div className={PIPEFITTER_TAB_LIST_WRAPPER_CLASS}>
                <div className="flex w-full min-w-0 items-center gap-1 bg-transparent pb-0">
                  {canScrollTabsLeft && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                      onClick={handleScrollTabsLeft}
                      aria-label="Scroll tabs left"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                  )}
                  <div
                    ref={tabsContainerRef}
                    className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  >
                    <TabsList className="h-auto w-max justify-start gap-4 bg-transparent px-0 py-0 md:gap-6 lg:gap-8">
                      {SUBTABS.map(({ key, label, icon: Icon }) => (
                        <TabsTrigger
                          key={key}
                          value={key}
                          className={`${PIPEFITTER_TAB_TRIGGER_CLASS} shrink-0 whitespace-nowrap`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {label}
                          </div>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                  {canScrollTabsRight && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                      onClick={handleScrollTabsRight}
                      aria-label="Scroll tabs right"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>

              <TabsContent value="pipeDetails" className="mt-4">
                <PipeDetails
                  project={project}
                  canEdit={canEdit}
                  canReport={canReport}
                  selectedPipe={selectedPipe}
                  onSelectionChange={setSelectedPipeDetailsFromTable}
                  pipeSizesData={pipeSizesData}
                />
              </TabsContent>

              <TabsContent value="sequence" className="mt-4">
                <Sequence
                  ref={sequenceRef}
                  project={project}
                  canEdit={canEdit}
                  canReport={canReport}
                  selectionRequest={sequenceSelectionRequest}
                  availablePipeSelections={selectedPipeDetailsFromTable}
                  pipeSpecification={pipeSpecificationForSequence}
                  selectedPipe={selectedPipe}
                />
              </TabsContent>

              <TabsContent value="optifit" className="mt-4">
                <OptifitResults project={project} selectedPipe={selectedPipe} />
              </TabsContent>

              <TabsContent value="visualization" className="mt-4">
                <Reports project={project} canReport={canReport} selectedPipe={selectedPipe} />
              </TabsContent>
            </Tabs>
          </>
        )}

        <GenerateSequenceModal
          open={isGenerateModalOpen}
          onOpenChange={setIsGenerateModalOpen}
          isSubmitting={isSequenceLoading}
          isAutofillLoading={isPipeDetailsLoading}
          selectedPipeCount={selectedPipeDetailsFromTable.length}
          pipeIds={pipeIds}
          sequences={sequenceOptions}
          sequenceModeOptions="new-regenerate"
          initialValues={generateSequenceInitialValues}
          pipeSpecification={pipeSpecificationForSequence}
          syncMetricsFromPipeSelection
          applyInitialValuesWhileOpen={false}
          onSubmit={(values) => {
            const mode = values.mode === 'new' ? 'NEW' : values.mode === 'regenerate' ? 'REGENERATE' : 'APPEND';
            const payload = {
              projectNumber,
              mode,
              sequenceName: values.sequenceName,
              pipeIds,
              ...(values.existingSequenceRef && Object.keys(values.existingSequenceRef).length > 0
                ? { existingSequenceRef: values.existingSequenceRef }
                : {}),
              config: values.config,
              ...(values.notifyEmail ? { notifyEmail: values.notifyEmail } : {}),
              ...(values.notifyEmails && values.notifyEmails.length > 0
                ? { notifyEmails: values.notifyEmails }
                : {}),
            };
            dispatch(
              pipefitterPipeBasedGenerateSequence(
                payload,
                () => {
                  setIsGenerateModalOpen(false);
                  setActiveSubtab('sequence');
                  if (values.existingSequenceRef?.spreadId) {
                    setSequenceSelectionRequest({
                      selectionType: 'spread',
                      id: Number(values.existingSequenceRef.spreadId),
                      token: Date.now(),
                    });
                  } else if (values.existingSequenceRef?.subSpreadId) {
                    setSequenceSelectionRequest({
                      selectionType: 'subSpread',
                      id: Number(values.existingSequenceRef.subSpreadId),
                      token: Date.now(),
                    });
                  } else {
                    setPendingGeneratedSequenceName((values.sequenceName ?? '').trim());
                  }
                  dispatch({ type: PIPEFITTER_GET_CONFIGURATION, payload: { projectNumber } });
                  queueMicrotask(() => {
                    sequenceRef.current?.refetchSequenceList?.();
                  });
                },
                () => {}
              )
            );
          }}
        />

        <GenerateReportTypeModal
          open={generateReportTypeModalOpen}
          onOpenChange={setGenerateReportTypeModalOpen}
          projectNumber={projectNumber}
          pipeLabel={
            selectedPipe?.displayName ??
            selectedPipe?.pipeName ??
            selectedPipe?.pipeId ??
            'this pipe'
          }
          onSelectPipeData={handleSelectPipeDataReport}
          onSelectPipeCompatibility={handleSelectPipeCompatibilityReport}
          onSelectRandomPipeCompatibility={handleSelectRandomPipeCompatibilityReport}
        />

        <PipeDataReportGenerateModal
          open={pipeDataReportGenerateModalOpen}
          onOpenChange={setPipeDataReportGenerateModalOpen}
          project={project}
          selectedPipe={selectedPipe}
          initialSelectedIds={selectedPipeStatusIds}
        />

        <PipeGroupPipesDialog
          open={overviewGroupDialogOpen}
          onOpenChange={setOverviewGroupDialogOpen}
          project={project}
          titleHint={projectName || projectNumber || 'Project'}
          pipeIds={overviewPipeIds}
          groupIds={overviewGroupIds}
          canEdit={canEdit}
          onUpdated={() => setOverviewRefreshToken((n) => n + 1)}
        />

        <PipeCompatibilityReportGenerateModal
          open={pipeCompatibilityReportGenerateModalOpen}
          onOpenChange={setPipeCompatibilityReportGenerateModalOpen}
          project={project}
          selectedPipe={selectedPipe}
          initialSelectedIds={selectedPipeStatusIds}
        />

        <RandomPipeCompatibilityModal
          open={randomPipeCompatibilityModalOpen}
          onOpenChange={setRandomPipeCompatibilityModalOpen}
          project={project}
          selectedPipe={selectedPipe}
        />

        <RandomPipeCompatHistoryModal
          open={randomPipeCompatHistoryModalOpen}
          onOpenChange={setRandomPipeCompatHistoryModalOpen}
          project={project}
          selectedPipe={selectedPipe}
        />

        <ProjectSettingsModal
          open={showSettingsModal}
          onOpenChange={setShowSettingsModal}
          projectNumber={projectNumber}
        />

        <Dialog
          open={showMeanMaxConfirmModal}
          onOpenChange={(open) => {
            if (!isMeanMaxSubmitting) setShowMeanMaxConfirmModal(open);
          }}
        >
          <DialogContent className="max-w-[500px] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                {isMeanMaxStatusChecking || isMeanMaxAlreadyRunning ? (
                  <Loader2 className="h-5 w-5 animate-spin text-amber-700" />
                ) : (
                  <Calculator className="h-5 w-5 text-amber-700" />
                )}
              </div>
              <DialogTitle className="text-xl font-semibold">Recalculate Mean/Max</DialogTitle>
            </div>
            <div className="mb-6 space-y-3">
              {isMeanMaxStatusChecking ? (
                <div className="flex items-center gap-3 text-gray-700">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-700 shrink-0" />
                  <p>Checking calculation status…</p>
                </div>
              ) : isMeanMaxAlreadyRunning ? (
                <>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Loader2 className="h-5 w-5 animate-spin text-amber-700 shrink-0" />
                    <p>
                      Mean/Max calculation is <span className="font-semibold">still running</span> for
                      this project.
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      Please wait until it finishes. You cannot start another calculation while one is
                      in progress.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700">
                    Recalculate mean/max for this project
                    {selectedGroupId !== undefined && selectedGroupId !== '' ? (
                      <>
                        {' '}
                        for group <span className="font-semibold">{selectedGroupId}</span>
                      </>
                    ) : selectedGroupId === '' ? (
                      <> for ungrouped pipes</>
                    ) : null}{' '}
                    using available (and used) pipes that are not archived?
                  </p>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      Existing mean/max calculation data
                      {selectedGroupId !== undefined && selectedGroupId !== ''
                        ? ` for group ${selectedGroupId}`
                        : selectedGroupId === ''
                          ? ' for ungrouped pipes'
                          : ' for this project'}{' '}
                      will be removed, then calculation will run again.
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMeanMaxConfirmModal(false)}
                disabled={isMeanMaxSubmitting}
                className="min-w-[100px]"
              >
                {isMeanMaxAlreadyRunning || isMeanMaxStatusChecking ? 'Close' : 'Cancel'}
              </Button>
              {!isMeanMaxStatusChecking && !isMeanMaxAlreadyRunning && (
                <Button
                  onClick={handleConfirmMeanMaxCalculation}
                  disabled={isMeanMaxSubmitting || !projectNumber?.trim()}
                  className="bg-hero-red text-white hover:bg-hero-red-60 min-w-[100px]"
                >
                  {isMeanMaxSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Starting…
                    </>
                  ) : (
                    'Confirm'
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PipefitterReportsModal
          isOpen={isReportsModalOpen}
          onClose={() => setIsReportsModalOpen(false)}
          project={project}
          selectedPipe={selectedPipe}
          canReport={canReport}
        />

        {showVersionsModal && (
          <PipefitterReportVersionsModal
            isOpen={showVersionsModal}
            onClose={() => setShowVersionsModal(false)}
            project={project}
            projectName={projectName}
            projectNumber={projectNumber}
            projectId={project?.project?.projectId}
            selectedPipe={selectedPipe}
            selectedPipeStatusIds={selectedPipeStatusIds}
          />
        )}

      </CardContent>
    </Card>
  );
};
